import { ForwardedRef, forwardRef } from "react";
import Select, { SelectInstance } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { useLazyQuery } from "@apollo/client";
import useSelectStyles from "utils/useSelectStyles";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import useFetchOptions from "components/Shared/Select/useFetchOptions";
import { BrokerFilter } from "__generated__/gql/graphql";

const OPTION_FRAGMENT_STR = /* GraphQL */ `
  fragment BrokerSelectOption on Broker {
    id
    name
  }
`;

type Option = DocumentType<typeof optionFragment>;

const optionFragment = gql(OPTION_FRAGMENT_STR);

const valuesQuery = gql(/* GraphQL */ `
  query BrokerSelectValue($ids: [UUID!]!) {
    brokersList(filter: { id: { in: $ids } }) {
      ...BrokerSelectOption
    }
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query BrokerSelectOptions($filter: BrokerFilter) {
    brokersList(filter: $filter, orderBy: NAME_ASC) {
      ...BrokerSelectOption
    }
  }
`);

function getOptions(data: DocumentType<typeof optionsQuery>) {
  return getFragmentData(optionFragment, data?.brokersList);
}

type Props<IsMulti extends boolean = false> = Omit<
  StateManagerProps<Option, IsMulti>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti> & {
    isForBrand?: boolean;
  };

function BrokerSelect<IsMulti extends boolean = false>(
  {
    isForBrand,
    isMulti,
    onChange,
    value: rawValue,
    isLoading,
    ...rest
  }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>();
  const brandId = useCurrentBrandId();

  const idValueProps = useIdValue<Option, IsMulti, typeof valuesQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.brokersList),
    isMulti,
    labelKey: "name",
    onChange,
    query: valuesQuery,
    value: rawValue,
  });

  const filter: BrokerFilter | undefined =
    isForBrand && brandId
      ? { brokerBrands: { some: { brandId: { equalTo: brandId } } } }
      : undefined;

  const lazyQuery = useLazyQuery(optionsQuery, {
    variables: { filter },
  });

  const fetchOptionsProps = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
    skip: !brandId,
  });

  return (
    <Select<Option, IsMulti>
      {...styleProps}
      {...idValueProps}
      {...fetchOptionsProps}
      {...{ isMulti, ref }}
      isLoading={
        idValueProps.isLoading || fetchOptionsProps.isLoading || isLoading
      }
      placeholder="Select a broker…"
      noOptionsMessage={() => "No brokers found"}
      {...rest}
    />
  );
}

export default forwardRef(BrokerSelect);
